import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  TextareaAutosize,
  CircularProgress,
  TextField,
  MenuItem,
  Grid,
  IconButton,
} from "@material-ui/core";
import { NoteAdd, Edit } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { contactsTypesActions } from "../../../../../store/contactTypes/contactTypesActions";

const useStyles = makeStyles(theme => ({
  container: {
    padding: "0px",
  },
  addButton: {
    marginBottom: "20px",
  },
  notesList: {
    marginBottom: "20px",
  },
  noteItem: {
    padding: "15px",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    marginBottom: "10px",
    backgroundColor: "#f9f9f9",
    position: "relative",
  },
  noteContent: {
    fontSize: "14px",
    color: "#333",
    marginBottom: "8px",
    whiteSpace: "pre-wrap",
  },
  noteMeta: {
    fontSize: "12px",
    color: "#666",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  noteType: {
    display: "inline-block",
    padding: "2px 8px",
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "500",
  },
  noteActions: {
    position: "absolute",
    top: "10px",
    right: "10px",
    display: "flex",
    gap: "5px",
  },
  actionButton: {
    padding: "4px",
    minWidth: "auto",
  },
  textAreaContainer: {
    marginTop: "0px",
  },
  formSection: {
    marginBottom: "20px",
    padding: "15px",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
  },
  sectionTitle: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "10px",
  },
  textArea: {
    width: "100%",
    minHeight: "120px",
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "14px",
    resize: "vertical",
    fontFamily: "inherit",
    "&:focus": {
      outline: "none",
      borderColor: "#3699ff",
    },
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "10px",
  },
  emptyState: {
    textAlign: "center",
    color: "#666",
    padding: "40px",
    fontSize: "16px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: "20px",
  },
  formTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "15px",
  },
  // Added styles for consistent field heights
  formField: {
    "& .MuiOutlinedInput-root": {
      height: "40px",
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
    },
  },
  dateField: {
    "& .MuiOutlinedInput-root": {
      height: "40px",
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
    },
    "& .MuiInputLabel-outlined": {
      transform: "translate(14px, 14px) scale(1)",
    },
    "& .MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.75)",
    },
  },
  formGrid: {
    marginBottom: "15px",
    alignItems: "center", // Align fields vertically
  },
  cancelButton: {
    textTransform: "none !important",
  },
}));

// Note types configuration
const NOTE_TYPES = [
  { value: 1, label: "Call" },
  { value: 2, label: "Email" },
  { value: 3, label: "Visit" },
];

const ProspectNotesTab = ({ entityId, entityType = "prospect" }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [notes, setNotes] = useState([]);
  const [showAddNote, setShowAddNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [newNote, setNewNote] = useState("");
  const [noteType, setNoteType] = useState(1);
  const [noteDate, setNoteDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch notes
  const fetchNotes = useCallback(async () => {
    if (!entityId) return;

    setIsLoading(true);
    try {
      const response = await dispatch(
        contactsTypesActions.onGetProspectNotes({
          prospectId: entityId,
          dateFrom: null,
          dateTo: null,
          pagination: {
            pageSize: 1000,
            pageNo: 0,
            orderByKey: "id",
            orderByVal: 0,
          },
        })
      );
      setIsLoading(false);
      setNotes(response?.data?.data?.data || []);
    } catch (error) {
      console.error("Error fetching notes:", error);
      setIsLoading(false);
    }
  }, [entityId, dispatch]);

  // Save note
  const saveNote = async () => {
    if (!newNote.trim()) {
      toast.error("Please enter a note");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        prospectId: entityId,
        comments: newNote.trim(),
        type: noteType,
        noteDate: noteDate || new Date().toISOString(),
      };

      // Add ID if editing existing note
      if (editingNote) {
        payload.id = editingNote.id;
      }

      const response = await dispatch(
        contactsTypesActions.onAddProspectNotes(payload)
      );

      if (response?.data?.success) {
        setNewNote("");
        setNoteType(1);
        setNoteDate("");
        setShowAddNote(false);
        setEditingNote(null);
        setIsSaving(false);
        toast.success(
          editingNote ? "Note updated successfully" : "Note saved successfully"
        );
        fetchNotes();
      } else {
        toast.error(response?.data?.message || "Failed to save note");
        setIsSaving(false);
      }
    } catch (error) {
      console.error("Error saving note:", error);
      setIsSaving(false);
      toast.error("Failed to save note");
    }
  };

  // Edit note with fixed date parsing
  const handleEditNote = note => {
    setEditingNote(note);
    setNewNote(note.comments || "");
    setNoteType(note.type || 1);

    // Fix date parsing - handle both "11-Nov-2025" and ISO formats
    if (note.notesDate) {
      try {
        const date = new Date(note.notesDate);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const formattedDate = `${year}-${month}-${day}`;
          setNoteDate(formattedDate);
        } else {
          setNoteDate("");
        }
      } catch (error) {
        console.error("Error parsing note date:", error);
        setNoteDate("");
      }
    } else {
      setNoteDate("");
    }

    setShowAddNote(true);
  };

  const handleAddNote = () => {
    setEditingNote(null);
    setNewNote("");
    setNoteType(1);
    setNoteDate("");
    setShowAddNote(true);
  };

  const handleCancel = () => {
    setNewNote("");
    setNoteType(1);
    setNoteDate("");
    setShowAddNote(false);
    setEditingNote(null);
  };

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  // Format date for display
  const formatDisplayDate = dateString => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const getTypeLabel = typeValue => {
    const type = NOTE_TYPES.find(t => t.value === typeValue);
    return type ? type.label : typeValue;
  };

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={24} />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Button
        variant='contained'
        color='primary'
        startIcon={<NoteAdd />}
        onClick={handleAddNote}
        className={classes.addButton}
        disabled={showAddNote}>
        Add Note
      </Button>

      {showAddNote && (
        <div className={classes.textAreaContainer}>
          <div className={classes.formTitle}>
            {editingNote ? "Edit Note" : "Add New Note"}
          </div>

          <div className={classes.formSection}>
            <div className={classes.sectionTitle}>Note Details</div>
            <Grid container spacing={2} className={classes.formGrid}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label='Note Type'
                  value={noteType}
                  onChange={e => setNoteType(parseInt(e.target.value))}
                  fullWidth
                  variant='outlined'
                  size='small'
                  className={classes.formField} // Added class for consistent height
                >
                  {NOTE_TYPES.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label='Note Date'
                  type='date'
                  value={noteDate}
                  onChange={e => setNoteDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    max: getTodayDate(),
                  }}
                  fullWidth
                  variant='outlined'
                  size='small'
                  className={classes.dateField} // Added class for consistent height
                />
              </Grid>
            </Grid>
          </div>

          <div className={classes.formSection}>
            <div className={classes.sectionTitle}>Note Content</div>
            <TextareaAutosize
              className={classes.textArea}
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              placeholder='Enter your note here...'
              disabled={isSaving}
              minRows={2}
            />
          </div>

          <div className={classes.actions}>
            <Button
              variant='outlined'
              onClick={handleCancel}
              className={classes.cancelButton}
              disabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant='contained'
              color='primary'
              onClick={saveNote}
              disabled={isSaving || !newNote.trim()}>
              {isSaving ? (
                <CircularProgress size={16} />
              ) : editingNote ? (
                "Update Note"
              ) : (
                "Save Note"
              )}
            </Button>
          </div>
        </div>
      )}

      <div className={classes.notesList}>
        {notes.length > 0 ? (
          notes.map(note => (
            <div key={note.id} className={classes.noteItem}>
              <div className={classes.noteActions}>
                <IconButton
                  className={classes.actionButton}
                  onClick={() => handleEditNote(note)}
                  size='small'>
                  <Edit fontSize='small' />
                </IconButton>
              </div>
              <div className={classes.noteContent}>{note.comments}</div>
              <div className={classes.noteMeta}>
                <div>
                  <span className={classes.noteType}>
                    {getTypeLabel(note.type)}
                  </span>
                </div>
                <div>{formatDisplayDate(note.notesDate)}</div>
              </div>
            </div>
          ))
        ) : (
          <div className={classes.emptyState}>
            No notes found. Add your first note!
          </div>
        )}
      </div>
    </div>
  );
};

export default ProspectNotesTab;
